import { put, select } from "redux-saga/effects";

import { ApplicationScope } from "@superblocksteam/shared";

import { runEventHandlers } from "legacy/actions/widgetActions";
import { EventType } from "legacy/constants/ActionConstants";
import type { ApiInfo } from "legacy/constants/ApiConstants";
import {
  getApiInfoById,
  getDeveloperPreferences,
} from "legacy/selectors/sagaSelectors";
import { createRunEventHandlersPayload } from "legacy/utils/actions";
import { findV2Error } from "legacy/utils/ApiNotificationUtility";
import {
  selectV2ApiById,
  selectV2ApiCancelledByTypeById,
  selectV2ApiRunCancelledById,
} from "store/slices/apisV2/selectors";
import { CancelledByType } from "store/slices/apisV2/slice";
import type {
  ApiExecutionSagaPayload,
  ExecutionResponse,
} from "store/slices/apisV2/types";
import { getV2ApiName } from "store/slices/apisV2/utils/getApiIdAndName";
import { addNewPromise } from "store/utils/resolveIdSingleton";

export type TriggerFrontendEventHandlersPayload = Pick<
  ApiExecutionSagaPayload,
  "callStack" | "spanId" | "manualRun"
> & {
  apiId: string;
  result: ExecutionResponse | undefined;
};

export function* triggerFrontendEventHandlersSaga({
  apiId,
  result,
  callStack,
  spanId,
  manualRun,
}: TriggerFrontendEventHandlersPayload) {
  const developerPreferences: ReturnType<typeof getDeveloperPreferences> =
    yield select(getDeveloperPreferences);
  const executionError = findV2Error(result);
  const systemError =
    result && "systemError" in result ? result.systemError : undefined;
  const hasError = executionError || systemError;

  const isCancelled: boolean = yield select(selectV2ApiRunCancelledById, apiId);

  const cancelledByType: ReturnType<typeof selectV2ApiCancelledByTypeById> =
    yield select(selectV2ApiCancelledByTypeById, apiId);

  const apiResponseInfo: ApiInfo | undefined = yield select(
    getApiInfoById,
    apiId,
  );

  const api: ReturnType<typeof selectV2ApiById> = yield select(
    selectV2ApiById,
    apiId,
  );
  const apiName = api ? getV2ApiName(api) : undefined;

  if (
    isCancelled &&
    cancelledByType === CancelledByType.MANUAL && // non-manual cancels are triggered elsewhere
    developerPreferences.application.triggerFrontendEventHandlersOnManualRun &&
    apiResponseInfo?.onCancel &&
    apiResponseInfo.onCancel.length > 0
  ) {
    const { callback, promise: waitForEvents } = callbackPromise();
    const callbackId = addNewPromise(callback);
    yield put(
      runEventHandlers(
        createRunEventHandlersPayload({
          steps: apiResponseInfo.onCancel,
          // TODO(API_SCOPE): update when custom events support app scope
          currentScope: ApplicationScope.PAGE,
          type: EventType.ON_CANCEL,
          entityName: apiName ?? "<API>",
          callStack: callStack || [],
          callbackId,
          spanId,
        }),
      ),
    );
    yield waitForEvents;
  } else if (
    !isCancelled &&
    !(
      manualRun &&
      !developerPreferences.application.triggerFrontendEventHandlersOnManualRun
    )
  ) {
    if (
      !hasError && // dont show success if there was an execution error or a system error
      apiResponseInfo?.onSuccess &&
      apiResponseInfo.onSuccess.length > 0
    ) {
      const { callback, promise: waitForEvents } = callbackPromise();
      const callbackId = addNewPromise(callback);
      yield put(
        runEventHandlers(
          createRunEventHandlersPayload({
            steps: apiResponseInfo.onSuccess,
            // TODO(API_SCOPE): update when custom events support app scope
            currentScope: ApplicationScope.PAGE,
            type: EventType.ON_SUCCESS,
            entityName: apiName ?? "<API>",
            callStack: callStack || [],
            callbackId,
            spanId,
          }),
        ),
      );
      yield waitForEvents;
    } else if (
      executionError && // only check execution error, not systemError because systemError's are handled elsewhere
      apiResponseInfo?.onError &&
      apiResponseInfo.onError.length > 0
    ) {
      const { callback, promise: waitForEvents } = callbackPromise();
      const callbackId = addNewPromise(callback);
      yield put(
        runEventHandlers(
          createRunEventHandlersPayload({
            steps: apiResponseInfo.onError,
            // TODO(API_SCOPE): update when custom events support app scope
            currentScope: ApplicationScope.PAGE,
            type: EventType.ON_ERROR,
            entityName: apiName ?? "<API>",
            callStack: callStack || [],
            callbackId,
            spanId,
            additionalNamedArguments: {
              error: executionError,
            },
          }),
        ),
      );
      yield waitForEvents;
    }
  }
}

type Result = {
  callback: () => void;
  promise: Promise<void>;
};

/**
 * Transforms a callback into a promise.
 * @example
 * const { callback, promise } = callbackPromise();
 * // do something async that eventually calls callback();
 * await promise;
 * // do something after the callback is called
 * @returns A function that returns a promise and a callback that resolves the promise.
 */
function callbackPromise(): Result {
  let resolvePromise: (() => void) | undefined;
  const promise = new Promise<void>((resolve) => {
    resolvePromise = resolve;
  });
  return {
    callback: () => resolvePromise?.(),
    promise,
  };
}
